import React, { CSSProperties, useEffect, useMemo, useState } from 'react';

import styles from './styles.module.scss';

const PIECES = 48;
const DURATION_MS = 3600;
const COLORS = ['var(--hub-primary)', 'var(--hub-success-text)', 'var(--hub-rating)', 'var(--lucky-point)', 'var(--hub-warning-border)'];

/**
 * A one-off burst of confetti inside its (relative, overflow-hidden) parent: CSS only, no library.
 * Skipped entirely when the member prefers reduced motion; gone after a few seconds.
 */
export const Confetti = (): React.ReactElement | null => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(false), DURATION_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const pieces = useMemo(
    () =>
      Array.from({ length: PIECES }, (_, index) => ({
        left: `${Math.random() * 100}%`,
        delay: `${Math.random() * 0.6}s`,
        duration: `${2.2 + Math.random() * 1.2}s`,
        drift: `${(Math.random() - 0.5) * 160}px`,
        spin: `${(Math.random() > 0.5 ? 1 : -1) * (360 + Math.random() * 540)}deg`,
        color: COLORS[index % COLORS.length],
        wide: index % 3 === 0,
      })),
    [],
  );

  if (!visible) {
    return null;
  }

  return (
    <div className={styles.confetti} aria-hidden="true">
      {pieces.map((piece, index) => (
        <span
          // Positional pieces; never reordered.
          key={index}
          className={piece.wide ? `${styles.confettiPiece} ${styles.confettiWide}` : styles.confettiPiece}
          style={
            {
              left: piece.left,
              background: piece.color,
              animationDelay: piece.delay,
              animationDuration: piece.duration,
              '--confetti-drift': piece.drift,
              '--confetti-spin': piece.spin,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
};
